import { useEffect, useRef, useState } from "react";
import { api, idr } from "../../lib/api";
import { toast } from "sonner";
import { Warehouse, TrendingUp, AlertTriangle, Wallet, Percent, Package, PackageX } from "lucide-react";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

// Awal bulan berjalan (WIB) — default rentang laporan bulan ini, bukan "hari ini".
const firstDayOfMonth = () => `${bulanIniISO()}-01`;

// GUDANG (inventory_warehouses) BUKAN cabang — satu gudang bisa melayani banyak
// cabang sekaligus. Halaman ini SENGAJA punya dua bagian: laba per gudang (dari
// jual-beli barang ber-IMEI yang tertaut ke gudangnya) dan rincian produk yang
// baris penjualannya rugi. Keduanya baca `/finance/laporan/gudang` &
// `/finance/laporan/produk-rugi` — lihat catatan lengkap di reports.py kenapa
// beban usaha TIDAK ikut dipecah per gudang (buku besar tidak menyimpannya).
export default function FinanceLabaGudang() {
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(hariIniISO());
  const [cabang, setCabang] = useState("");
  const BRANCHES = useCabangOptions({ denganKosong: true });

  const [gudangData, setGudangData] = useState(null);
  const [rugiData, setRugiData] = useState(null);
  const [gudangFilter, setGudangFilter] = useState(""); // filter tabel produk rugi ke satu gudang
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // Nomor urut request — filter yang diklik beruntun tidak boleh membiarkan
  // respons lama menimpa hasil filter aktif (pola sama dengan FinanceLabaKategori.jsx).
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setNotFound(false);
    setErrMsg("");
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (cabang) p.set("cabang", cabang);
    const pRugi = new URLSearchParams(p);
    if (gudangFilter) pRugi.set("gudang_id", gudangFilter);
    Promise.all([
      api.get(`/finance/laporan/gudang?${p}`),
      api.get(`/finance/laporan/produk-rugi?${pRugi}`),
    ])
      .then(([rg, rr]) => {
        if (seq !== reqSeq.current) return;
        setGudangData(rg.data);
        setRugiData(rr.data);
      })
      .catch(e => {
        if (seq !== reqSeq.current) return;
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          setErrMsg(e?.response?.data?.detail || "Gagal memuat laporan laba per gudang");
          toast.error(e?.response?.data?.detail || "Gagal memuat laporan laba per gudang");
        }
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dateFrom, dateTo, cabang, gudangFilter]);

  const perGudang = gudangData?.per_gudang || [];
  const total = gudangData?.total || { omzet: 0, hpp: 0, laba_kotor: 0, qty: 0 };
  const items = rugiData?.items || [];
  const modalTdk = rugiData?.modal_tidak_diketahui || { count: 0, qty: 0, omzet: 0 };
  // Opsi dropdown gudang diambil dari data yang SUDAH dimuat (bukan endpoint
  // terpisah) — halaman ini digerbangi `finance_laporan`, bukan izin Inventory,
  // jadi tidak boleh bergantung ke `/inventory/warehouses` yang gerbangnya beda.
  const gudangOptions = perGudang.filter(g => g.gudang_id);

  // Kedua tabel di halaman ini adalah hasil AGREGASI backend (dikelompokkan
  // per gudang / per produk×gudang), bukan potongan N-teratas dari query
  // mentah — aman disortir tanpa BatasDaftarNotice. `<tfoot>` total tetap di
  // luar array yang disortir, jadi selalu di baris paling bawah.
  const gudangSort = useSortState(null);
  const sortedPerGudang = sortRows(perGudang, gudangSort.sortKey, gudangSort.sortDir, {
    gudang: (g) => g.gudang || "",
    qty: (g) => g.qty || 0,
    omzet: (g) => g.omzet || 0,
    hpp: (g) => g.hpp || 0,
    laba: (g) => g.laba_kotor || 0,
    margin: (g) => g.margin_kotor_pct || 0,
  });

  const rugiSort = useSortState(null);
  const sortedItems = sortRows(items, rugiSort.sortKey, rugiSort.sortDir, {
    produk: (it) => it.produk || "",
    gudang: (it) => it.gudang || "",
    kejadian: (it) => it.jumlah_kejadian || 0,
    qty: (it) => it.qty_total || 0,
    harga_jual: (it) => it.rata2_harga_jual || 0,
    modal: (it) => it.rata2_modal || 0,
    kerugian: (it) => it.total_kerugian || 0,
    terakhir: (it) => it.tanggal_terakhir || "",
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="finance-laba-gudang">
      <header>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan &amp; Kontrol</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Laba per Gudang</h1>
        <p className="text-sm text-slate-600 mt-1">
          Omzet, HPP, dan laba kotor dipecah per gudang sumber stok — plus daftar produk yang baris penjualannya rugi.
        </p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom} data-testid="laba-gd-from" className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo} data-testid="laba-gd-to" className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Cabang</span>
          <select value={cabang} onChange={e => setCabang(e.target.value)}
            data-testid="laba-gd-cabang"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Cabang"}</option>)}
          </select>
        </label>
      </div>

      {notFound && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3" data-testid="laba-gd-notfound">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Laporan belum tersedia</p>
            <p className="text-xs text-amber-800 mt-1">
              Endpoint laporan laba per gudang belum aktif di server. Coba lagi setelah backend selesai deploy.
            </p>
          </div>
        </div>
      )}

      {!notFound && (
        <LoadingState loading={loading} error={errMsg || null} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-laba-gudang">
          <>
            {/* Ringkasan total */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={TrendingUp} label="Omzet" value={idr(total.omzet)} tint="slate" />
              <Kpi icon={Wallet} label="HPP" value={idr(total.hpp)} tint="amber" />
              <Kpi icon={Percent} label="Laba Kotor" value={idr(total.laba_kotor)} tint={total.laba_kotor < 0 ? "rose" : "emerald"} />
              <Kpi icon={Package} label="Qty Terjual" value={total.qty ?? 0} tint="sky" />
            </div>

            {/* Peringatan: beban tidak bisa dipecah per gudang — jangan sampai
                pembaca mengira laba_kotor di sini = laba bersih gudang. */}
            {gudangData?.beban_catatan && (
              <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-800" data-testid="laba-gd-beban-catatan">
                <b>Perhatian:</b> {gudangData.beban_catatan}
                {gudangData.beban_perusahaan_total != null && (
                  <p className="mt-1 font-mono">Beban seluruh perusahaan (konteks, bukan per gudang): {idr(gudangData.beban_perusahaan_total)}</p>
                )}
              </div>
            )}

            {/* Tabel per gudang */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Warehouse className="w-4 h-4 text-emerald-600" />
                <h3 className="font-display font-semibold text-slate-900">Rincian per Gudang</h3>
              </div>
              <table className="w-full text-sm" data-testid="laba-gd-tabel">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <SortTh label="Gudang" sortKeyName="gudang" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} testid="laba-gd-sort-gudang" />
                    <SortTh label="Qty" sortKeyName="qty" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} align="right" testid="laba-gd-sort-qty" />
                    <SortTh label="Omzet" sortKeyName="omzet" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} align="right" testid="laba-gd-sort-omzet" />
                    <SortTh label="HPP" sortKeyName="hpp" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} align="right" testid="laba-gd-sort-hpp" />
                    <SortTh label="Laba Kotor" sortKeyName="laba" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} align="right" testid="laba-gd-sort-laba" />
                    <SortTh label="Marjin" sortKeyName="margin" sortKey={gudangSort.sortKey} sortDir={gudangSort.sortDir} onSort={gudangSort.toggle} align="right" testid="laba-gd-sort-margin" />
                  </tr>
                </thead>
                <tbody>
                  {perGudang.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-sm text-slate-400" data-testid="laba-gd-empty">
                        Tidak ada data pada rentang &amp; cabang ini.
                      </td>
                    </tr>
                  )}
                  {sortedPerGudang.map(g => (
                    <tr key={g.gudang_id || "unknown"} className={`border-t border-slate-100 hover:bg-slate-50 ${!g.gudang_id ? "bg-slate-50/60" : ""}`} data-testid={`laba-gd-row-${g.gudang_id || "unknown"}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">
                        {g.gudang}
                        {!g.modal_lengkap && (
                          <span className="ml-2 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Modal belum lengkap</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">{g.qty}</td>
                      <td className="py-3 px-4 text-right font-mono">{idr(g.omzet)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{idr(g.hpp)}</td>
                      <td className={`py-3 px-4 text-right font-mono font-semibold ${g.laba_kotor < 0 ? "text-rose-600" : "text-emerald-700"}`}>{idr(g.laba_kotor)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{g.margin_kotor_pct}%</td>
                    </tr>
                  ))}
                </tbody>
                {perGudang.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                      <td className="py-3 px-4 text-slate-900">Total</td>
                      <td className="py-3 px-4 text-right font-mono">{total.qty ?? 0}</td>
                      <td className="py-3 px-4 text-right font-mono">{idr(total.omzet)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{idr(total.hpp)}</td>
                      <td className={`py-3 px-4 text-right font-mono ${(total.laba_kotor || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{idr(total.laba_kotor)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{total.omzet ? ((total.laba_kotor / total.omzet) * 100).toFixed(1) : "0.0"}%</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ============ Bagian B: Produk Dijual Rugi ============ */}
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
                    <PackageX className="w-4 h-4 text-rose-600" /> Produk Dijual Rugi
                  </h2>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Baris penjualan dengan harga jual di bawah modal — hanya barang yang modalnya DIKETAHUI, dikelompokkan per produk × gudang.
                  </p>
                </div>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Filter Gudang</span>
                  <select value={gudangFilter} onChange={e => setGudangFilter(e.target.value)}
                    data-testid="laba-gd-rugi-gudang-filter"
                    className="mt-1 h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[200px]">
                    <option value="">Semua Gudang (termasuk tidak diketahui)</option>
                    {gudangOptions.map(g => <option key={g.gudang_id} value={g.gudang_id}>{g.gudang}</option>)}
                  </select>
                </label>
              </div>

              {modalTdk.count > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="laba-gd-rugi-modal-tdk">
                  <b>{modalTdk.count} baris penjualan</b> (qty {modalTdk.qty}, omzet {idr(modalTdk.omzet)}) TIDAK dievaluasi untung/rugi
                  karena modalnya belum diketahui saat itu tercatat — bukan berarti untung, bukan berarti rugi. Tidak dimasukkan ke daftar di bawah.
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="laba-gd-rugi-tabel">
                  <thead>
                    <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <SortTh label="Produk" sortKeyName="produk" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} testid="laba-gd-rugi-sort-produk" />
                      <SortTh label="Gudang" sortKeyName="gudang" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} testid="laba-gd-rugi-sort-gudang" />
                      <SortTh label="Kejadian" sortKeyName="kejadian" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-kejadian" />
                      <SortTh label="Qty" sortKeyName="qty" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-qty" />
                      <SortTh label="Rata² Harga Jual" sortKeyName="harga_jual" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-hargajual" />
                      <SortTh label="Rata² Modal" sortKeyName="modal" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-modal" />
                      <SortTh label="Total Kerugian" sortKeyName="kerugian" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-kerugian" />
                      <SortTh label="Terakhir" sortKeyName="terakhir" sortKey={rugiSort.sortKey} sortDir={rugiSort.sortDir} onSort={rugiSort.toggle} align="right" testid="laba-gd-rugi-sort-terakhir" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-10 text-sm text-slate-400" data-testid="laba-gd-rugi-empty">
                          Tidak ada produk yang dijual rugi pada rentang &amp; filter ini. 🎉
                        </td>
                      </tr>
                    )}
                    {sortedItems.map((it, i) => (
                      <tr key={`${it.sku || it.produk}-${it.gudang_id || "unk"}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`laba-gd-rugi-row-${i}`}>
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{it.produk}</p>
                          {it.sku && <p className="text-[10px] font-mono text-slate-400">{it.sku}</p>}
                        </td>
                        <td className="py-3 px-4 text-slate-700">{it.gudang}</td>
                        <td className="py-3 px-4 text-right font-mono">{it.jumlah_kejadian}</td>
                        <td className="py-3 px-4 text-right font-mono">{it.qty_total}</td>
                        <td className="py-3 px-4 text-right font-mono">{idr(it.rata2_harga_jual)}</td>
                        <td className="py-3 px-4 text-right font-mono text-slate-600">{idr(it.rata2_modal)}</td>
                        <td className="py-3 px-4 text-right font-mono font-semibold text-rose-600">{idr(it.total_kerugian)}</td>
                        <td className="py-3 px-4 text-right text-xs text-slate-500">{formatTanggal(it.tanggal_terakhir)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {items.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                        <td colSpan={6} className="py-3 px-4 text-slate-900">Total Kerugian ({rugiData?.jumlah_baris ?? items.length} baris)</td>
                        <td className="py-3 px-4 text-right font-mono text-rose-600">{idr(rugiData?.total_kerugian || 0)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        </LoadingState>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
